/**
 * <countdown-timer> — image-with-text countdown block
 *
 * Modes:
 *   data-mode="fixed"      — counts down to data-target ISO datetime
 *   data-mode="per_user"   — counts down from first view; remaining time stored
 *                            per user in localStorage so it persists across visits
 *
 * Required attrs:
 *   data-section-id, data-block-id  — used to namespace localStorage
 *
 * Children:
 *   [data-countdown-days]   [data-countdown-hours]
 *   [data-countdown-minutes][data-countdown-seconds]
 *   [data-countdown-expired]
 */
class CountdownTimer extends HTMLElement {
  connectedCallback() {
    if (this._initialised) return;
    this._initialised = true;

    const mode = this.dataset.mode;
    const sectionId = this.dataset.sectionId || '';
    const blockId = this.dataset.blockId || '';

    let endTime = null;

    if (mode === 'fixed') {
      const target = (this.dataset.target || '').trim();
      if (!target) return;
      const parsed = new Date(target);
      if (isNaN(parsed.getTime())) return;
      endTime = parsed.getTime();
    } else if (mode === 'per_user') {
      const storageKey = 'mk_countdown_' + sectionId + '_' + blockId;
      const days = parseInt(this.dataset.userDays, 10) || 0;
      const hours = parseInt(this.dataset.userHours, 10) || 0;
      const minutes = parseInt(this.dataset.userMinutes, 10) || 0;
      const durationMs = ((days * 24 + hours) * 60 + minutes) * 60 * 1000;
      if (durationMs <= 0) return;

      let saved = null;
      try { saved = localStorage.getItem(storageKey); } catch (e) {}

      if (saved && !isNaN(parseInt(saved, 10))) {
        endTime = parseInt(saved, 10);
      } else {
        endTime = Date.now() + durationMs;
        try { localStorage.setItem(storageKey, String(endTime)); } catch (e) {}
      }
    } else {
      return;
    }

    this.endTime = endTime;
    this.daysEl = this.querySelector('[data-countdown-days]');
    this.hoursEl = this.querySelector('[data-countdown-hours]');
    this.minutesEl = this.querySelector('[data-countdown-minutes]');
    this.secondsEl = this.querySelector('[data-countdown-seconds]');
    this.expiredEl = this.querySelector('[data-countdown-expired]');

    this._tick();
    this._intervalId = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    if (this._intervalId) {
      clearInterval(this._intervalId);
      this._intervalId = null;
    }
  }

  _tick() {
    const remaining = Math.max(0, this.endTime - Date.now());

    if (remaining === 0) {
      this.setAttribute('data-state', 'expired');
      if (this.expiredEl) this.expiredEl.hidden = false;
      if (this._intervalId) {
        clearInterval(this._intervalId);
        this._intervalId = null;
      }
    }

    const totalSeconds = Math.floor(remaining / 1000);
    const days = Math.floor(totalSeconds / 86400);
    const hours = Math.floor((totalSeconds % 86400) / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;

    if (this.daysEl)    this.daysEl.textContent    = String(days).padStart(2, '0');
    if (this.hoursEl)   this.hoursEl.textContent   = String(hours).padStart(2, '0');
    if (this.minutesEl) this.minutesEl.textContent = String(minutes).padStart(2, '0');
    if (this.secondsEl) this.secondsEl.textContent = String(seconds).padStart(2, '0');
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}